import { useState, useEffect, useCallback, useRef } from 'react'

interface VADOptions {
  onSpeechStart?: () => void
  onSpeechEnd?: () => void
}

interface UseVADCDNReturn {
  isVADSupported: boolean
  isVADInitialized: boolean
  isListening: boolean
  isSpeaking: boolean
  isInitializing: boolean
  error: string | null
  startListening: () => Promise<void>
  stopListening: () => void
  testEnvironment: () => Promise<{ isNoisy: boolean; noiseLevel: 'low' | 'moderate' | 'high'; recommendation: string }>
}

// Declare global vad variable
declare global {
  interface Window {
    vad?: {
      MicVAD: {
        new: (options: any) => Promise<any>
      }
    }
  }
}

export const useVADCDN = (options: VADOptions = {}): UseVADCDNReturn => {
  const [isVADSupported] = useState(() => {
    return !!(window.AudioContext || (window as any).webkitAudioContext) && !!navigator.mediaDevices?.getUserMedia
  })
  
  const [isVADInitialized, setIsVADInitialized] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const vadInstanceRef = useRef<any>(null)
  const testVadRef = useRef<any>(null)
  const speechEventsRef = useRef<{ time: number; isSpeech: boolean }[]>([])
  const testTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scriptsLoadedRef = useRef(false)

  const cleanup = useCallback(() => {
    if (vadInstanceRef.current) {
      try {
        vadInstanceRef.current.pause()
        vadInstanceRef.current = null
      } catch (e) {
        console.warn('Error pausing VAD instance:', e)
      }
    }
    
    if (testVadRef.current) {
      try {
        testVadRef.current.pause()
        testVadRef.current = null
      } catch (e) {
        console.warn('Error pausing test VAD:', e)
      }
    }
    
    if (testTimeoutRef.current) {
      clearTimeout(testTimeoutRef.current)
      testTimeoutRef.current = null
    }
    
    setIsVADInitialized(false)
    setIsListening(false)
    setIsSpeaking(false)
    setIsInitializing(false)
  }, [])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  const loadVADScripts = useCallback(async (): Promise<boolean> => {
    if (scriptsLoadedRef.current && window.vad) {
      return true
    }

    return new Promise((resolve) => {
      const loadScript = (src: string, onLoad: () => void, onError: () => void) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`)
        if (existingScript) {
          onLoad()
          return
        }

        const script = document.createElement('script')
        script.src = src
        script.onload = onLoad
        script.onerror = onError
        document.head.appendChild(script)
      }

      // Load ONNX Runtime first (like the original implementation)
      loadScript(
        'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.14.0/dist/ort.js',
        () => {
          console.log('ONNX Runtime loaded')
          
          // Then load VAD (using same version as original)
          loadScript(
            'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.7/dist/bundle.min.js',
            () => {
              console.log('VAD library loaded')
              scriptsLoadedRef.current = true
              resolve(true)
            },
            () => {
              console.error('Failed to load VAD library')
              resolve(false)
            }
          )
        },
        () => {
          console.error('Failed to load ONNX Runtime')
          resolve(false)
        }
      )
    })
  }, [])

  const startListening = useCallback(async () => {
    if (!isVADSupported) {
      setError('VAD is not supported in this browser')
      return
    }

    if (isListening || vadInstanceRef.current) {
      return
    }

    try {
      setIsInitializing(true)
      setError(null)

      // Load VAD scripts first
      const loaded = await loadVADScripts()
      if (!loaded || !window.vad) {
        throw new Error('Failed to load VAD scripts')
      }

      // Initialize VAD like in the original implementation
      vadInstanceRef.current = await window.vad.MicVAD.new({
        onSpeechStart: () => {
          setIsSpeaking(true)
          options.onSpeechStart?.()
        },
        onSpeechEnd: () => {
          setIsSpeaking(false)
          options.onSpeechEnd?.()
        },
        // Use similar settings to original
        positiveSpeechThreshold: 0.8,
        negativeSpeechThreshold: 0.65,
        minSpeechFrames: 5,
        preSpeechPadFrames: 1,
        redemptionFrames: 8,
        frameSamples: 1536,
        energyThreshold: 0.8,
        smoothingCoefficient: 0.7,
      })

      await vadInstanceRef.current.start()
      
      setIsVADInitialized(true)
      setIsListening(true)
      setIsInitializing(false)
      
      console.log('VAD initialized successfully using CDN approach')
    } catch (err) {
      console.error('Failed to initialize VAD:', err)
      setError(err instanceof Error ? err.message : 'Failed to initialize VAD')
      setIsInitializing(false)
      cleanup()
    }
  }, [isVADSupported, isListening, options, cleanup, loadVADScripts])

  const stopListening = useCallback(() => {
    cleanup()
  }, [cleanup])

  const testEnvironment = useCallback(async (): Promise<{ isNoisy: boolean; noiseLevel: 'low' | 'moderate' | 'high'; recommendation: string }> => {
    return new Promise(async (resolve) => {
      if (!isVADSupported) {
        resolve({
          isNoisy: false,
          noiseLevel: 'low',
          recommendation: 'VAD not supported - environment test unavailable'
        })
        return
      }

      try {
        setIsInitializing(true)
        speechEventsRef.current = []

        // Load VAD scripts first
        const loaded = await loadVADScripts()
        if (!loaded || !window.vad) {
          throw new Error('Failed to load VAD scripts')
        }

        // Create test VAD with more sensitive settings
        testVadRef.current = await window.vad.MicVAD.new({
          onSpeechStart: () => {
            speechEventsRef.current.push({ time: Date.now(), isSpeech: true })
            console.log('Test VAD: Speech detected')
          },
          onSpeechEnd: () => {
            speechEventsRef.current.push({ time: Date.now(), isSpeech: false })
            console.log('Test VAD: Speech ended')
          },
          // Very sensitive settings to catch any background noise/music
          positiveSpeechThreshold: 0.3, // Very low threshold to catch music
          negativeSpeechThreshold: 0.2, // Even lower
          minSpeechFrames: 1, // Catch very short sounds
          preSpeechPadFrames: 1,
          redemptionFrames: 2, // Short redemption
          frameSamples: 1536,
          energyThreshold: 0.4, // Lower energy threshold for music
          smoothingCoefficient: 0.3, // Less smoothing to catch more variations
        })

        await testVadRef.current.start()
        console.log('Test VAD started, testing for 6 seconds...')

        testTimeoutRef.current = setTimeout(async () => {
          try {
            if (testVadRef.current) {
              testVadRef.current.pause()
              testVadRef.current = null
            }
          } catch (e) {
            console.warn('Error stopping test VAD:', e)
          }

          setIsInitializing(false)

          const speechStartEvents = speechEventsRef.current.filter(event => event.isSpeech)
          const totalEvents = speechEventsRef.current.length
          
          console.log(`Test complete: ${speechStartEvents.length} speech events, ${totalEvents} total events`)

          // Analysis based on speech detection events with more flexible thresholds
          let isNoisy = false
          let noiseLevel: 'low' | 'moderate' | 'high' = 'low'
          let recommendation = ''

          if (speechStartEvents.length >= 8) {
            // Very many detections = high noise, definitely problematic
            isNoisy = true
            noiseLevel = 'high'
            recommendation = 'Environment is very noisy. Speech recognition may be significantly affected. Strong recommendation to use headphones or text mode.'
          } else if (speechStartEvents.length >= 4) {
            // Moderate detections = moderate noise, may affect quality
            isNoisy = true
            noiseLevel = 'moderate'
            recommendation = 'Background noise detected. Speech recognition accuracy may be reduced. Headphones recommended for better performance.'
          } else if (speechStartEvents.length >= 1) {
            // Few detections = low noise, acceptable with consent
            isNoisy = true
            noiseLevel = 'moderate'
            recommendation = 'Minimal background noise detected. Speech mode should work well, but headphones can improve accuracy.'
          } else {
            // No detections = quiet
            noiseLevel = 'low'
            recommendation = 'Excellent! Your environment is very quiet and perfect for speech mode.'
          }

          console.log(`Environment analysis: ${speechStartEvents.length} speech events detected = ${noiseLevel.toUpperCase()} noise level`)

          resolve({ isNoisy, noiseLevel, recommendation })
        }, 6000)

      } catch (error) {
        console.error('Environment test failed:', error)
        setIsInitializing(false)
        resolve({
          isNoisy: false,
          noiseLevel: 'high',
          recommendation: 'Environment test failed. Please ensure microphone access is granted and try again.'
        })
      }
    })
  }, [isVADSupported, loadVADScripts])

  return {
    isVADSupported,
    isVADInitialized,
    isListening,
    isSpeaking,
    isInitializing,
    error,
    startListening,
    stopListening,
    testEnvironment
  }
}